import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { ChevronDown, ChevronRight, CheckCircle2, Circle, Clock, Calendar, FileText, Info } from "lucide-react";

const STAGE_GROUPS = [
  { major: "1", title: "Drafting", color: "var(--accent)", keys: ["1a_drafting"] },
  { major: "2", title: "Quality Checking", color: "var(--amber)", keys: ["2a_exegesis", "2b_basic_pt", "2c_team_check"] },
  { major: "3", title: "Community Checking", color: "#6a5b8f", keys: ["3a_community"] },
  { major: "4", title: "Quality Validation", color: "#8f5b34", keys: ["4a_back_trans", "4b_qa_validation", "4c_advanced_pt"] },
  { major: "5", title: "Church Acceptance", color: "var(--on-track)", keys: ["5a_read_aloud", "5b_church_accept", "5c_audio", "5d_publish_prep", "5e_dbl", "5f_text_pub"] },
];

const STAGE_LABEL = {
  "1a_drafting": "Drafting",
  "2a_exegesis": "Exegesis",
  "2b_basic_pt": "Basic PT",
  "2c_team_check": "Team Check",
  "3a_community": "Community Check",
  "4a_back_trans": "Back Translation",
  "4b_qa_validation": "QA Validation",
  "4c_advanced_pt": "Advanced PT",
  "5a_read_aloud": "Read Aloud",
  "5b_church_accept": "Church Acceptance",
  "5c_audio": "Audio Recording",
  "5d_publish_prep": "Publish Prep",
  "5e_dbl": "DBL Publishing",
  "5f_text_pub": "Text Publication",
};

function statusFor(stage) {
  if (!stage) return "pending";
  const s = (stage.status || "").toLowerCase();
  if (s.includes("complet")) return "done";
  if (s.includes("progress") || s.includes("started")) return "active";
  if (s.includes("not started") || s === "") return "pending";
  return s ? "active" : "pending";
}

function StageDot({ status, small }) {
  const size = small ? 14 : 18;
  if (status === "done")
    return <CheckCircle2 size={size} className="text-[color:var(--on-track)]" strokeWidth={1.75} />;
  if (status === "active")
    return <Clock size={size} className="text-[color:var(--amber)]" strokeWidth={1.75} />;
  return <Circle size={size} className="text-[color:var(--ink-3)]" strokeWidth={1.5} />;
}

export default function BookStages({ projectId }) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openBook, setOpenBook] = useState(null);

  useEffect(() => {
    api.get(`/language-projects/${projectId}/book-stages`)
      .then((r) => setBooks(r.data.books))
      .finally(() => setLoading(false));
  }, [projectId]);

  const summary = useMemo(() => {
    if (!books.length) return null;
    const completed = books.filter((b) => (b.current_stage || "").toLowerCase().includes("all stages complete")).length;
    const inProg = books.length - completed;
    return { total: books.length, completed, inProg };
  }, [books]);

  if (loading) return <div className="mono text-xs text-[color:var(--ink-2)] p-6">Loading stage data…</div>;
  if (!books.length) {
    return (
      <div className="hairline bg-white p-6 mt-6 text-center" data-testid="no-stages">
        <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-2">
          Translation Stages
        </div>
        <div className="text-sm text-[color:var(--ink-2)]">
          No stage-level data available yet for this language.
        </div>
      </div>
    );
  }

  return (
    <div className="hairline bg-white p-6 mt-6" data-testid="book-stages">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-2)] mb-1">
            Translation Stages · KAIROS
          </div>
          <div className="serif text-2xl tracking-tight">Book-by-Book Stage Timeline</div>
        </div>
        {summary && (
          <div className="mono text-[11px] uppercase tracking-widest text-[color:var(--ink-2)] flex gap-4">
            <span><span className="text-[color:var(--on-track)] font-semibold">{summary.completed}</span> complete</span>
            <span><span className="text-[color:var(--amber)] font-semibold">{summary.inProg}</span> in progress</span>
            <span><span className="text-[color:var(--ink)] font-semibold">{summary.total}</span> total books</span>
          </div>
        )}
      </div>

      {/* Stage legend */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-2 border border-[color:var(--border)] p-3 bg-[color:var(--surface-2)]">
        {STAGE_GROUPS.map((g) => (
          <div key={g.major} className="flex items-center gap-2">
            <div className="w-2 h-8" style={{ background: g.color }} />
            <div>
              <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-2)]">Stage {g.major}</div>
              <div className="text-xs font-medium">{g.title}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        {books.map((b) => {
          const isOpen = openBook === b.book_key;
          const stages = b.stages || {};
          const pct = Math.round((b.pct || 0) * 100);
          const doneStages = STAGE_GROUPS.flatMap((g) => g.keys).filter((k) => statusFor(stages[k]) === "done").length;
          const totalStages = STAGE_GROUPS.reduce((s, g) => s + g.keys.length, 0);
          return (
            <div key={b.book_key} className="border border-[color:var(--border)]" data-testid={`book-row-${b.book}`}>
              <button
                onClick={() => setOpenBook(isOpen ? null : b.book_key)}
                className="w-full flex items-center gap-4 px-4 py-3 hover:bg-[color:var(--surface-2)] text-left"
              >
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <div className="w-32 flex-shrink-0">
                  <div className="serif text-lg tracking-tight">{b.book}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-[color:var(--ink-3)]">
                    {b.total_verses || "—"} verses
                  </div>
                </div>
                {/* Compact stage strip */}
                <div className="flex-1 flex items-center gap-0.5 min-w-0 overflow-hidden">
                  {STAGE_GROUPS.map((g) => (
                    <div key={g.major} className="flex items-center gap-0.5">
                      {g.keys.map((k) => {
                        const st = statusFor(stages[k]);
                        return (
                          <div
                            key={k}
                            title={`${STAGE_LABEL[k]}: ${stages[k]?.status || "Not Started"}`}
                            className="h-6 w-6 flex items-center justify-center"
                            style={{
                              background: st === "done" ? g.color : st === "active" ? "var(--surface-2)" : "transparent",
                              border: st === "done" ? "none" : `1px solid ${st === "active" ? g.color : "var(--border)"}`,
                            }}
                          >
                            {st === "done" ? <CheckCircle2 size={10} className="text-white" strokeWidth={2.5} />
                              : st === "active" ? <Clock size={10} style={{ color: g.color }} strokeWidth={2} />
                              : null}
                          </div>
                        );
                      })}
                      <div className="w-2" />
                    </div>
                  ))}
                </div>
                <div className="w-40 flex-shrink-0 text-right">
                  <div className="mono text-sm">{pct}%</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-3)]">
                    {b.current_stage || `${doneStages}/${totalStages} stages`}
                  </div>
                </div>
              </button>
              {isOpen && <StageDetail stages={stages} totalVerses={b.total_verses} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StageDetail({ stages, totalVerses }) {
  return (
    <div className="border-t border-[color:var(--border)] bg-[color:var(--bg)] p-5" data-testid="stage-detail">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {STAGE_GROUPS.map((g) => (
          <div key={g.major} className="border border-[color:var(--border)] bg-white">
            <div className="px-3 py-2 border-b border-[color:var(--border)]" style={{ borderTopColor: g.color, borderTopWidth: 3 }}>
              <div className="mono text-[9px] uppercase tracking-widest text-[color:var(--ink-2)]">Stage {g.major}</div>
              <div className="text-sm font-semibold">{g.title}</div>
            </div>
            <div className="p-3 space-y-3">
              {g.keys.map((k) => {
                const st = stages[k] || {};
                const status = statusFor(st);
                return (
                  <div key={k} className="text-xs" data-testid={`stage-${k}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <StageDot status={status} small />
                      <div className="font-medium">{STAGE_LABEL[k]}</div>
                    </div>
                    <div className="pl-6 space-y-0.5 text-[color:var(--ink-2)]">
                      <div className="mono text-[10px]">
                        {st.status || <span className="italic text-[color:var(--ink-3)]">Not Started</span>}
                      </div>
                      {st.start_date && (
                        <div className="flex items-center gap-1 text-[10px]">
                          <Calendar size={9} /> Start {st.start_date}
                        </div>
                      )}
                      {st.complete_date && (
                        <div className="flex items-center gap-1 text-[10px]">
                          <CheckCircle2 size={9} /> Done {st.complete_date}
                        </div>
                      )}
                      {st.chapter_verses && (
                        <div className="flex items-center gap-1 text-[10px] mono">
                          <FileText size={9} /> {st.chapter_verses} verses
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
